import { useState, useRef, useCallback, useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import TeacherShell from '@/components/layout/TeacherShell'
import { Button } from '@/components/ui'

export default function Trial({ assessment, round, current_band, total_rounds, items_served_so_far, items }) {
  const [selections, setSelections] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [showInstructions, setShowInstructions] = useState(round === 1 && items_served_so_far === 0)
  const startTimesRef = useRef({})

  // Reset state and scroll to top when round changes (Inertia may preserve the component)
  useEffect(() => {
    setSelections({})
    setSubmitting(false)
    startTimesRef.current = {}
    const main = document.querySelector('main')
    if (main) main.scrollTop = 0
  }, [round])

  const trackStart = useCallback((questionId) => {
    if (!startTimesRef.current[questionId]) {
      startTimesRef.current[questionId] = Date.now()
    }
  }, [])

  const handleSelect = (questionId, optionIndex) => {
    trackStart(questionId)
    setSelections(prev => ({ ...prev, [questionId]: optionIndex }))
  }

  const allAnswered = items.every(item => selections[item.id] !== undefined)

  const handleSubmit = () => {
    if (!allAnswered || submitting) return
    setSubmitting(true)

    const responses = items.map(item => ({
      question_id: item.id,
      selected_index: selections[item.id],
      response_time_ms: startTimesRef.current[item.id]
        ? Date.now() - startTimesRef.current[item.id]
        : 0,
    }))

    router.post(`/teacher/assessments/${assessment.id}/trial_respond`, {
      responses,
    }, {
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/assessments/${assessment.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{assessment.title}</Link>{'  ›  Trial'}</>} maxWidth="narrow">
      {/* Progress header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Round {round} of {total_rounds}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Band: {current_band} · {items_served_so_far} items answered
          </span>
        </div>
        <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-300"
            style={{ width: `${(round / total_rounds) * 100}%` }}
          />
        </div>
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
          Preview mode — results will not be saved
        </p>
      </div>

      {/* Instructions */}
      {showInstructions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">How this test works</h2>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 mb-5">
            <li>You will see a series of vocabulary questions across {total_rounds} rounds.</li>
            <li>Each question shows a word used in a short sentence. Choose the answer that best matches the meaning of the <strong>bolded</strong> word.</li>
            <li>The test adapts to your level — questions will get harder or easier based on your answers.</li>
            <li>Select one answer per question, then click <strong>Submit &amp; Continue</strong> to move to the next round.</li>
            <li>It's okay if you don't know a word — just choose your best guess.</li>
          </ul>
          <Button onClick={() => setShowInstructions(false)}>
            Begin
          </Button>
        </div>
      )}

      {/* Items */}
      {!showInstructions && (<>
        <div className="space-y-8">
          {items.map((item) => {
            trackStart(item.id)
            return (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <p
                  className="text-lg text-gray-900 dark:text-white mb-5 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatPrompt(item.prompt) }}
                />

                <div className="space-y-2">
                  {item.options.map((opt, optIdx) => {
                    const selected = selections[item.id] === optIdx
                    return (
                      <button
                        key={opt.key}
                        onClick={() => handleSelect(item.id, optIdx)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                          selected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400 text-blue-900 dark:text-blue-100'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500 mr-2">
                          {String.fromCharCode(65 + optIdx)}.
                        </span>
                        {opt.text}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* Submit */}
        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!allAnswered || submitting}
          >
            {submitting ? 'Submitting...' : round >= total_rounds ? 'Finish' : 'Submit & Continue'}
          </Button>
        </div>
      </>)}
    </TeacherShell>
  )
}

function formatPrompt(prompt) {
  return prompt.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
}
